// client/src/components/ClientCamera.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { firestore, storage } from '../../firebase';
import {
  doc,
  updateDoc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
  limit as fsLimit
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../CSS/Dashboard.css';
import '../CSS/camera.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import jsQR from 'jsqr';

/* =========================================
   Cache léger (hybride)
========================================= */
const TTL_COMPANY_FID_MS = 15 * 60 * 1000;  // 15 min : statut fidélité d’une entreprise
const SETTINGS_CACHE_KEY = 'clientSettings'; // {email,pseudo,company?}
const INV_SCAN_TTL_MS = 15 * 60 * 1000;
// Firestore rules: list queries require limit <= 50
const INV_LIMIT_SCAN = 50;
const INV_CACHE_KEY = (email) => `inv_cache_${email}`;
const INV_KNOWN_COMPANIES_KEY = (email) => `inv_known_companies_${email}`;

const cacheGetJSON = (key, ttlMs) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!ttlMs) return obj;
    if (typeof obj !== 'object' || obj === null) return null;
    const ts = obj.ts || 0;
    if (Date.now() - ts > ttlMs) return null;
    return obj;
  } catch {
    return null;
  }
};
const cachePutJSON = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...payload, ts: Date.now() }));
  } catch {}
};
const putCache = cachePutJSON;
const fromCache = (key, ttl) => cacheGetJSON(key, ttl);
const companyFidKey = (uid) => `cache:companyFidActive:${uid}`; // {active:boolean}

/* =========================================
   Utils
========================================= */
const safeURL = (maybeUrl) => {
  try { return new URL(maybeUrl); } catch { return null; }
};

const getCachedPseudo = () => {
  // Préfère le cache Settings déjà posé ailleurs dans l’app
  const cached = cacheGetJSON(SETTINGS_CACHE_KEY, null);
  if (cached && typeof cached.pseudo === 'string') return cached.pseudo;
  return null;
};

/* =========================================
   Composant
========================================= */
const ClientCamera = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Aperçu conservé pour compat (non affiché)
  // eslint-disable-next-line
  const [imageURL, setImageURL] = useState(null);

  // Caméra / scan
  // eslint-disable-next-line
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [validationFailure, setValidationFailure] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [isCompanyLinked, setIsCompanyLinked] = useState(null);

  // Locks anti-doubles
  const qrBusyRef = useRef(false);

  const cameraContainerRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const allowCaptureRef = useRef(true);
  const companyUnsubsRef = useRef({});
  const companyAcceptedRef = useRef({});
  const companyScanLockRef = useRef(false);
  const lastCompanyEmailRef = useRef('');

  // Mode (normal | transfer) via query param (compat ancien flux)
  const search = new URLSearchParams(location.search);
  const mode = search.get('mode'); // 'transfer' | null

  /* =========================================
     Mount / Unmount
  ========================================= */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      closeCamera();
      Object.values(companyUnsubsRef.current).forEach((fn) => fn && fn());
      companyUnsubsRef.current = {};
    };
  }, []);

  /* =========================================
     Caméra helpers
  ========================================= */
  const closeCamera = () => {
    ['camera-container', 'capture-container'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        const video = el.querySelector('video');
        if (video && video.srcObject) {
          video.srcObject.getTracks().forEach(t => t.stop());
        }
        el.remove();
      }
    });
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setIsCameraOpen(false);
  };

  const scanQRCode = (video) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    scanIntervalRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(data.data, data.width, data.height);
        if (code && !qrBusyRef.current) {
          qrBusyRef.current = true; // lock
          clearInterval(scanIntervalRef.current);
          processQRCode(code.data).finally(() => {
            // si échec, on relancera le scan dans processQRCode()
          });
        }
      }
    }, 500);
  };

  const dataURLToBlob = (dataURL) => {
    const parts = dataURL.split(',');
    if (parts.length !== 2) return null;
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const binary = atob(parts[1]);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) array[i] = binary.charCodeAt(i);
    return new Blob([array], { type: mime });
  };

  /* =========================================
     QR: vérification fidélité avec cache 15 min
  ========================================= */
  const isCompanyFidelityActive = useCallback(async (clientUid) => {
    if (!clientUid) return false;

    // 1) Cache
    const cached = cacheGetJSON(companyFidKey(clientUid), TTL_COMPANY_FID_MS);
    if (cached && typeof cached.active === 'boolean') return cached.active;

    // 2) Lecture Firestore : d'abord par ID de document (cas le plus courant),
    //    puis fallback par champ userId (doc créé avec un ID différent)
    try {
      const directSnap = await getDoc(doc(firestore, 'entreprises', clientUid));
      if (directSnap.exists()) {
        const active = directSnap.data()?.fidelity === 'active';
        cachePutJSON(companyFidKey(clientUid), { active });
        return active;
      }

      // Fallback : l'ID du doc entreprise != UID commerçant
      // limit(1) requis par les règles Firestore (request.query.limit <= 50)
      const qRef = query(
        collection(firestore, 'entreprises'),
        where('userId', '==', clientUid),
        where('fidelity', '==', 'active'),
        fsLimit(1)
      );
      const qs = await getDocs(qRef);
      const active = !qs.empty;
      cachePutJSON(companyFidKey(clientUid), { active });
      return active;
    } catch {
      // En cas d'erreur réseau → fail-safe false
      return false;
    }
  }, []);

  /* =========================================
     Affiliation entreprise (reprise logique ClientSettings)
  ========================================= */
  const stopCompanyListeners = useCallback(() => {
    Object.values(companyUnsubsRef.current).forEach((fn) => fn && fn());
    companyUnsubsRef.current = {};
    companyAcceptedRef.current = {};
  }, []);

  const recomputeCompanyLink = useCallback(() => {
    const has = Object.values(companyAcceptedRef.current).some(Boolean);
    setIsCompanyLinked(has);
    allowCaptureRef.current = has;
  }, []);

  const attachCompanyListener = useCallback((companyId, email) => {
    if (!companyId || !email || companyUnsubsRef.current[companyId]) return;
    const compRef = doc(firestore, 'entreprises', companyId);
    companyUnsubsRef.current[companyId] = onSnapshot(
      compRef,
      (snap) => {
        const data = snap.data() || {};
        const invitations = Array.isArray(data.invitations) ? data.invitations : [];
        const accepted = invitations.some(
          (inv) => inv?.employeeEmail === email && inv?.status === 'accepted'
        );
        companyAcceptedRef.current[companyId] = accepted;
        recomputeCompanyLink();
      },
      (err) => console.error('company listener error:', err)
    );
  }, [recomputeCompanyLink]);

  const discoverCompanyLink = useCallback(async (email) => {
    if (!email) {
      stopCompanyListeners();
      setIsCompanyLinked(false);
      allowCaptureRef.current = false;
      lastCompanyEmailRef.current = '';
      return;
    }

    if (companyScanLockRef.current) {
      if (lastCompanyEmailRef.current === email) return;
      companyScanLockRef.current = false;
    }
    stopCompanyListeners();
    lastCompanyEmailRef.current = email;

    const cachedSettings = fromCache(SETTINGS_CACHE_KEY, null);
    if (cachedSettings?.company?.id) {
      companyAcceptedRef.current[cachedSettings.company.id] = true;
      recomputeCompanyLink();
    }

    const cachedInv = fromCache(INV_CACHE_KEY(email), INV_SCAN_TTL_MS);
    if (cachedInv?.companies?.length) {
      if (typeof cachedInv.hasCompany === 'boolean') {
        setIsCompanyLinked(cachedInv.hasCompany);
        allowCaptureRef.current = cachedInv.hasCompany;
      }
      cachedInv.companies.forEach((id) => attachCompanyListener(id, email));
      if (!navigator.onLine || cachedInv.hasCompany === true) return;
    }

    if (companyScanLockRef.current) return;
    companyScanLockRef.current = true;

    try {
      const entreprisesRef = collection(firestore, 'entreprises');
      const snap = await getDocs(query(entreprisesRef, fsLimit(INV_LIMIT_SCAN)));
      const companies = new Set();
      let hasAccepted = false;

      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const arr = Array.isArray(data.invitations) ? data.invitations : [];

        const accepted = arr.some((inv) => inv?.employeeEmail === email && inv?.status === 'accepted');
        if (accepted) hasAccepted = true;

        const pending = arr.some((inv) => inv?.employeeEmail === email && inv?.status === 'pending');
        if (accepted || pending) companies.add(docSnap.id);
        companyAcceptedRef.current[docSnap.id] = accepted;
      });

      setIsCompanyLinked(hasAccepted);
      allowCaptureRef.current = hasAccepted;
      const out = { companies: Array.from(companies), hasCompany: hasAccepted };
      putCache(INV_CACHE_KEY(email), out);
      putCache(INV_KNOWN_COMPANIES_KEY(email), { companies: out.companies });
      out.companies.forEach((id) => attachCompanyListener(id, email));
    } catch (err) {
      console.error('company link scan error:', err);
      setIsCompanyLinked((prev) => (prev === null ? false : prev));
      allowCaptureRef.current = false;
    } finally {
      companyScanLockRef.current = false;
    }
  }, [attachCompanyListener, recomputeCompanyLink, stopCompanyListeners]);

  /* =========================================
     Process QR (hybride + verrous + relance)
  ========================================= */
  const processQRCode = async (qrData) => {
    setQrLoading(true);

    const parsed = safeURL(qrData);
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('Utilisateur non authentifié');

      // ====== NOUVEAU : redirection de transfert (recommandé)
      // /redirectqrctransfertfid/<offerId>  (ou ?offer=)
      if (parsed && parsed.pathname.replace(/\/+$/, '').startsWith('/redirectqrctransfertfid')) {
        const parts = parsed.pathname.replace(/\/+$/, '').split('/');
        const offerId = parts[2] || parsed.searchParams.get('offer') || '';
        if (!offerId) throw new Error('Offre invalide');
        closeCamera();
        navigate(`/transfertfid?offer=${encodeURIComponent(offerId)}&mode=claim`);
        return;
      }

      // ====== Compat ancien : /transferoffer/<offerId>
      if (parsed && parsed.pathname.replace(/\/+$/, '').startsWith('/transferoffer')) {
        const parts = parsed.pathname.replace(/\/+$/, '').split('/');
        const offerId = parts[2] || '';
        if (!offerId) throw new Error('Offre invalide');
        closeCamera();
        navigate(`/transfertfid?offer=${encodeURIComponent(offerId)}&mode=claim`);
        return;
      }

      // ====== Compat : /transferfid?to=<uid> (mode "transfer")
      if (mode === 'transfer' && parsed && parsed.pathname.replace(/\/+$/, '') === '/transferfid') {
        const toUid = parsed.searchParams.get('to');
        if (!toUid) throw new Error('QR transfert invalide (paramètre "to" manquant)');
        closeCamera();
        navigate(`/transfertfid?to=${encodeURIComponent(toUid)}`);
        return;
      }

      // ====== Flux EXISTANT : QR entreprise (redirectqrc)
      // Accepte UID brut ou https://ecobill.app/redirectqrc/<uid>
      let clientUid = qrData;
      if (parsed && parsed.hostname === 'ecobill.app' && parsed.pathname.startsWith('/redirectqrc/')) {
        clientUid = parsed.pathname.split('/').pop();
      }

      // Vérification fidélité via cache (15 min)
      const ok = await isCompanyFidelityActive(clientUid);
      if (!ok) throw new Error('Fidélité inactive ou introuvable');

      closeCamera();
      navigate(`/clientscanqrc/${encodeURIComponent(clientUid)}`);
      return;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Erreur traitement QR :', err);
      setValidationFailure(true);
      setTimeout(() => setValidationFailure(false), 1000);

      // Relance douce du scan si la vue est toujours ouverte
      const container = cameraContainerRef.current;
      const video = container?.querySelector('video');
      if (video && mountedRef.current) {
        // petit délai pour éviter boucles serrées
        setTimeout(() => {
          if (!mountedRef.current) return;
          qrBusyRef.current = false; // déverrouille
          scanQRCode(video);
        }, 600);
      } else {
        qrBusyRef.current = false;
      }
    } finally {
      setQrLoading(false);
    }
  };

  /* =========================================
     Auth + détection affiliation + ouverture caméra
  ========================================= */
  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged((currentUser) => {
      if (!currentUser) {
        navigate('/');
        return;
      }
      const email = currentUser.email || '';
      discoverCompanyLink(email);
    });
    return () => unsub();
  }, [discoverCompanyLink, navigate]);

  useEffect(() => {
    if (isCompanyLinked === null) return;
    allowCaptureRef.current = isCompanyLinked;
    handleCapturePhoto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompanyLinked]);

  /* =========================================
     Capture & UI overlay
  ========================================= */
  const handleCapturePhoto = async (allowCapture = allowCaptureRef.current) => {
    if (isCameraOpen) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });
      if (!mountedRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      setIsCameraOpen(true);

      const container = document.createElement('div');
      container.id = 'camera-container';
      container.className = 'camera-overlay';
      cameraContainerRef.current = container;
      document.body.appendChild(container);

      const videoWrapper = document.createElement('div');
      videoWrapper.className = 'camera-frame';
      container.appendChild(videoWrapper);

      const video = document.createElement('video');
      video.autoplay = true;
      video.playsInline = true;
      video.srcObject = stream;
      video.controls = false;
      video.className = 'camera-feed';
      videoWrapper.appendChild(video);

      await new Promise(res => (video.onloadedmetadata = () => video.play().then(res)));

      // Démarrer le scan QR en continu (500ms)
      scanQRCode(video);

      const bar = document.createElement('div');
      bar.className = 'camera-bottom-bar';
      container.appendChild(bar);

      const createButton = ({ variant, ariaLabel, innerHTML, glyph, onClick }) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `camera-bottom-bar__button ${variant || ''}`.trim();
        button.setAttribute('aria-label', ariaLabel || 'action');
        button.innerHTML = innerHTML || `<span class="camera-bottom-bar__icon">${glyph || ''}</span>`;
        button.addEventListener('click', onClick);
        bar.appendChild(button);
        return button;
      };

      createButton({
        variant: 'camera-bottom-bar__button--ghost camera-bottom-bar__button--close',
        ariaLabel: 'Fermer',
        innerHTML: '<span class="camera-bottom-bar__icon">✕</span>',
        onClick: () => {
          closeCamera();
          navigate('/clientdocs');
        }
      });

      if (allowCapture) {
        createButton({
          variant: 'camera-bottom-bar__button--capture',
          ariaLabel: 'Capturer une photo',
          innerHTML: '<span class="camera-bottom-bar__capture"><span></span></span>',
          onClick: () => captureImage(video, stream)
        });
      }

      createButton({
        variant: 'camera-bottom-bar__button--ghost camera-bottom-bar__button--switch',
        ariaLabel: 'Changer de caméra',
        innerHTML: '<span class="camera-bottom-bar__icon">⟳</span>',
        onClick: () => {
          closeCamera();
          handleCapturePhoto(allowCapture);
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Erreur accès caméra :', err);
      alert('Impossible d’accéder à la caméra. Vérifiez vos permissions et HTTPS.');
    }
  };

  // === Flux "photo de profil" (fonctionnalité initiale conservée) ===
  const captureImage = (video, stream) => {
    const rect = video.getBoundingClientRect();
    const arVideo = video.videoWidth / video.videoHeight;
    const arVisible = rect.width / rect.height;
    let sx, sy, sw, sh;
    if (arVideo > arVisible) {
      sh = video.videoHeight;
      sw = sh * arVisible;
      sx = (video.videoWidth - sw) / 2;
      sy = 0;
    } else {
      sw = video.videoWidth;
      sh = sw / arVisible;
      sx = 0;
      sy = (video.videoHeight - sh) / 2;
    }

    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const previewBlob = dataURLToBlob(dataUrl);
    if (!previewBlob) return;
    setImageURL(dataUrl);
    stream.getTracks().forEach(t => t.stop());

    closeCamera();
    const captureContainer = document.createElement('div');
    captureContainer.id = 'capture-container';
    captureContainer.className = 'camera-overlay';
    document.body.appendChild(captureContainer);

    const previewWrapper = document.createElement('div');
    previewWrapper.className = 'camera-frame camera-frame--preview';
    captureContainer.appendChild(previewWrapper);

    const img = document.createElement('img');
    img.src = dataUrl;
    img.alt = 'Aperçu de la capture';
    img.className = 'camera-preview';
    previewWrapper.appendChild(img);

    const actions = document.createElement('div');
    actions.className = 'camera-bottom-bar camera-bottom-bar--overlay';
    captureContainer.appendChild(actions);

    const createBtn = ({ variant, ariaLabel, innerHTML, glyph, onClick }) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `camera-bottom-bar__button ${variant || ''}`.trim();
      button.setAttribute('aria-label', ariaLabel || 'action');
      button.innerHTML = innerHTML || `<span class="camera-bottom-bar__icon">${glyph || ''}</span>`;
      button.addEventListener('click', onClick);
      actions.appendChild(button);
      return button;
    };

    createBtn({
      variant: 'camera-bottom-bar__button--ghost camera-bottom-bar__button--close',
      ariaLabel: 'Annuler et revenir aux documents',
      innerHTML: '<span class="camera-bottom-bar__icon">✕</span>',
      onClick: () => {
        captureContainer.remove();
        navigate('/clientdocs');
      }
    });

    createBtn({
      variant: 'camera-bottom-bar__button--approve',
      ariaLabel: 'Valider cette photo',
      innerHTML: '<span class="camera-bottom-bar__icon">✔</span>',
      onClick: async () => {
        setUploading(true);
        const success = await handleValidatePhoto(previewBlob);
        if (success) {
          captureContainer.remove();
          navigate('/clientinvoices');
        } else {
          alert('Erreur lors de l’upload. Veuillez réessayer.');
        }
      }
    });

    createBtn({
      variant: 'camera-bottom-bar__button--ghost camera-bottom-bar__button--switch',
      ariaLabel: 'Reprendre une photo',
      innerHTML: '<span class="camera-bottom-bar__icon">⟳</span>',
      onClick: () => {
        captureContainer.remove();
        handleCapturePhoto();
      }
    });
  };

  /* =========================================
     Upload photo (hybride : 0 lecture si pseudo en cache)
  ========================================= */
  const handleValidatePhoto = async (blob) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Utilisateur non authentifié');
      const userId = user.uid;

      // 1) Pseudo : on tente le cache, sinon 1 lecture Firestore
      let userPseudo = getCachedPseudo();
      if (!userPseudo) {
        try {
          const userSnap = await getDoc(doc(firestore, 'users', userId));
          userPseudo = userSnap.data()?.pseudo || 'Inconnu';
          // hydrate le cache settings pour usages futurs
          const cachedSet = cacheGetJSON(SETTINGS_CACHE_KEY, null) || {};
          cachePutJSON(SETTINGS_CACHE_KEY, { ...cachedSet, pseudo: userPseudo, email: cachedSet.email || user.email });
        } catch {
          userPseudo = 'Inconnu';
        }
      }

      // 2) Upload Storage
      const photoPath = `${userId}/photos/${userId}-${Date.now()}.jpg`;
      const photoRef = ref(storage, photoPath);
      await uploadBytes(photoRef, blob, {
        contentType: blob.type || 'image/jpeg',
        customMetadata: {
          category: 'perso',
          TakenByEmail: user.email || '',
          TakenByUID: userId,
          TakenByPseudo: userPseudo
        }
      });

      // 3) (Optionnel) URL publique dans users.photoURL — best-effort
      try {
        const downloadURL = await getDownloadURL(photoRef);
        await updateDoc(doc(firestore, 'users', userId), { photoURL: downloadURL });
      } catch (e) {
        // best-effort, ne bloque pas l’UX
        // eslint-disable-next-line no-console
        console.warn('update photoURL best-effort:', e);
      }

      // 4) Invalide le cache des photos pour forcer la réval dans les vues
      try {
        localStorage.removeItem('userPhotos');
        localStorage.removeItem('userPhotos_lastSync');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Impossible de rafraîchir le cache photo', e);
      }

      setValidationSuccess(true);
      setUploading(false);
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Erreur upload photo :', err);
      setUploading(false);
      return false;
    }
  };

  /* =========================================
     Rendu (overlays)
  ========================================= */
  return (
    <div>
      {/* Overlays */}
      {validationSuccess && (
        <div className="camera-feedback camera-feedback--success">
          <FaCheckCircle />
        </div>
      )}
      {validationFailure && (
        <div className="camera-feedback camera-feedback--error">
          <FaTimesCircle />
        </div>
      )}
      {(qrLoading || uploading) && (
        <div className="camera-feedback camera-feedback--loading">
          <FaSpinner className="spin" />
        </div>
      )}
    </div>
  );
};

export default ClientCamera;
